/**
 * Centralized utility for file export naming convention across the application.
 * Ensures downloaded Excel (.xlsx) and CSV (.csv) files include From Date and To Date
 * in the format: [ReportName]_[DD-MM-YYYY]_to_[DD-MM-YYYY].[ext]
 * Example: Summary_01-09-2026_to_07-09-2026.xlsx
 */

/**
 * Parses any common date representation into a JavaScript Date object.
 * Handles DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, ISO timestamps, and formatted strings like '10Jan, 2026'.
 */
export const parseDateValue = (val) => {
  if (!val) return null;
  if (val instanceof Date && !isNaN(val.getTime())) return val;

  const str = String(val).trim();

  // Match DD-MM-YYYY or DD/MM/YYYY
  const dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10) - 1;
    const year = parseInt(dmyMatch[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Match YYYY-MM-DD
  const ymdMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (ymdMatch) {
    const year = parseInt(ymdMatch[1], 10);
    const month = parseInt(ymdMatch[2], 10) - 1;
    const day = parseInt(ymdMatch[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Fallback to standard Date parsing
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d;

  return null;
};

/**
 * Formats a date into DD-MM-YYYY string.
 * Example: 2026-09-01 -> "01-09-2026"
 */
export const formatDateDDMMYYYY = (dateInput) => {
  const d = parseDateValue(dateInput);
  if (!d) return null;

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Extracts a date range string formatted as `[FromDate]_to_[ToDate]` (e.g. `01-09-2026_to_07-09-2026`).
 * Priority:
 * 1. Explicit fromDate and toDate arguments if supplied
 * 2. Range scanned from the data items (earliest date to latest date)
 * 3. Fallback date (or today's date)
 */
export const getDateRangeFromData = (data = [], fromDate = null, toDate = null, fallbackDate = null) => {
  let fromStr = formatDateDDMMYYYY(fromDate);
  let toStr = formatDateDDMMYYYY(toDate);

  if (fromStr && toStr) {
    return `${fromStr}_to_${toStr}`;
  }

  // Scan items in data to find minimum and maximum dates
  const timestamps = [];
  if (Array.isArray(data) && data.length > 0) {
    data.forEach((item) => {
      if (!item) return;
      const raw =
        item.date ||
        item.Date ||
        item.legDate ||
        item.tripDate ||
        item.trip_date ||
        item.tripdate ||
        item.uploadDate ||
        item.upload_date ||
        item.createdAt ||
        item.created_at ||
        item.deletedOn;

      const parsed = parseDateValue(raw);
      if (parsed) {
        timestamps.push(parsed.getTime());
      }
    });
  }

  if (timestamps.length > 0) {
    timestamps.sort((a, b) => a - b);
    const minD = new Date(timestamps[0]);
    const maxD = new Date(timestamps[timestamps.length - 1]);
    if (!fromStr) fromStr = formatDateDDMMYYYY(minD);
    if (!toStr) toStr = formatDateDDMMYYYY(maxD);
  }

  // Fallback if dates could not be derived from data
  if (!fromStr || !toStr) {
    const defaultDateStr =
      formatDateDDMMYYYY(fallbackDate) || formatDateDDMMYYYY(new Date());
    if (!fromStr) fromStr = defaultDateStr;
    if (!toStr) toStr = defaultDateStr;
  }

  return `${fromStr}_to_${toStr}`;
};

/**
 * Generates the full file name including date range.
 * Example: getExportFileName({ reportName: "Summary", data: rows, ext: "xlsx" })
 * => "Summary_01-09-2026_to_07-09-2026.xlsx"
 */
export const getExportFileName = ({
  reportName = 'Report',
  data = [],
  fromDate = null,
  toDate = null,
  fallbackDate = null,
  ext = 'xlsx',
}) => {
  const dateRange = getDateRangeFromData(data, fromDate, toDate, fallbackDate);
  const cleanExt = ext.replace(/^\./, '');
  return `${reportName}_${dateRange}.${cleanExt}`;
};
